import React, { useEffect, useMemo, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import axios from 'axios';
import API_ROUTES from '../../../api';
import Icon from '../../../icon';
import CardReasonPicker from './CardReasonPicker';

const RECORD_ACTIONS = [
    { value: 3, label: '进球记录', icon: Icon.GOAL, tone: 'goal' },
    { value: 4, label: '乌龙球', icon: Icon.OWN_GOAL, tone: 'own-goal' },
    { value: 5, label: '黄牌', icon: Icon.YELLOW_CARD, tone: 'yellow' },
    { value: 6, label: '红牌', icon: Icon.RED_CARD, tone: 'red' },
    { value: 7, label: '换人', icon: Icon.UP_AND_DOWN_ICON, tone: 'swap' },
    { value: 0, label: '受伤', icon: Icon.SHOUSHANG, tone: 'injury' },
];

const GOAL_OPTIONS = [
    { value: 13, label: '左脚进球', icon: '⚽', openPlay: true },
    { value: 14, label: '右脚进球', icon: '⚽', openPlay: true },
    { value: 11, label: '头球进球', icon: '🧠', openPlay: true },
    { value: 3, label: '其他方式', icon: '✨', openPlay: true },
    { value: 19, label: '任意球·左脚', icon: '🎯' },
    { value: 15, label: '任意球·右脚', icon: '🎯' },
    { value: 20, label: '角球·左脚', icon: '🚩' },
    { value: 16, label: '角球·右脚', icon: '🚩' },
    { value: 21, label: '点球·左脚', icon: '🥅' },
    { value: 12, label: '点球·右脚', icon: '🥅' },
];

const TeamButtons = ({ gameDetail, selected, onSelect }) => (
    <div className="referee-record-team-grid">
        {[
            { value: 1, label: gameDetail?.greenName || '绿队', color: gameDetail?.greenColor || '#637ef5' },
            { value: 2, label: gameDetail?.redName || '红队', color: gameDetail?.redColor || '#ef5f68' },
        ].map(team => (
            <button
                key={team.value}
                type="button"
                className={selected === team.value ? 'active' : ''}
                style={{ '--record-team-color': team.color }}
                onClick={() => onSelect(team.value)}
            >
                <i />
                <strong>{team.label}</strong>
                {selected === team.value && <span>✓</span>}
            </button>
        ))}
    </div>
);

const PlayerButton = ({ member, selected, getMemberImage, onClick }) => (
    <button
        type="button"
        className={`referee-record-player${selected ? ' active' : ''}`}
        onClick={onClick}
    >
        <img src={getMemberImage(member)} alt="" />
        <strong>{member.name}</strong>
        {selected && <em>✓</em>}
    </button>
);

const TeamPlayerGroups = ({ gameDetail, teamPlayers, selectedId, getMemberImage, onSelect }) => {
    const selectedMember = [...(teamPlayers[1] || []), ...(teamPlayers[2] || [])]
        .find(member => member.id === selectedId);
    // 选中球员后只展示其所属队伍，再次点击同一球员即可取消并恢复双方队伍。
    const visibleTeams = selectedMember ? [selectedMember.gameTeam] : [1, 2];

    return (
        <div className="referee-record-player-groups">
            {selectedMember && (
                <div className="referee-record-team-focus-tip">
                    已聚焦当前队伍，再次点击 <strong>{selectedMember.name}</strong> 可取消选择
                </div>
            )}
            {visibleTeams.map(team => {
            const players = teamPlayers[team] || [];
            if (!players.length) return null;
            const color = team === 1 ? (gameDetail?.greenColor || '#637ef5') : (gameDetail?.redColor || '#ef5f68');
            const name = team === 1 ? (gameDetail?.greenName || '绿队') : (gameDetail?.redName || '红队');
            return (
                <div key={team} className="referee-record-player-group">
                    <div className="referee-record-team-label" style={{ '--record-team-color': color }}>
                        <i />{name}<span>{players.length}人</span>
                    </div>
                    <div className="referee-record-player-grid">
                        {players.map(member => (
                            <PlayerButton
                                key={member.id}
                                member={member}
                                selected={selectedId === member.id}
                                getMemberImage={getMemberImage}
                                onClick={() => onSelect(member.id)}
                            />
                        ))}
                    </div>
                </div>
            );
            })}
        </div>
    );
};

const MultiPlayerGrid = ({ members, selectedIds, getMemberImage, onToggle }) => (
    <div className="referee-record-player-grid">
        {members.map(member => (
            <PlayerButton
                key={member.id}
                member={member}
                selected={selectedIds.includes(member.id)}
                getMemberImage={getMemberImage}
                onClick={() => onToggle(member.id)}
            />
        ))}
    </div>
);

// 右侧口哨入口的所有数据记录步骤集中在一个底部上滑面板内。
const RefereeRecordPopup = ({
    visible,
    gameDetail,
    memberListInfo,
    getMemberImage,
    onClose,
    onUpdated,
}) => {
    const [action, setAction] = useState(null);
    const [goalEventId, setGoalEventId] = useState(null);
    const [playerId, setPlayerId] = useState(null);
    const [assistMemberId, setAssistMemberId] = useState(null);
    const [cardReason, setCardReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [substitutionTeam, setSubstitutionTeam] = useState(null);
    const [downMemberIds, setDownMemberIds] = useState([]);
    const [upMemberIds, setUpMemberIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setAction(null);
        setGoalEventId(null);
        setPlayerId(null);
        setAssistMemberId(null);
        setCardReason('');
        setCustomReason('');
        setSubstitutionTeam(null);
        setDownMemberIds([]);
        setUpMemberIds([]);
        setSubmitting(false);
    }, [visible]);

    const teamMembers = useMemo(() => {
        const result = { 1: [], 2: [] };
        (memberListInfo || [])
            .filter(item => item.gameType === 0 && [1, 2].includes(item.gameTeam))
            .sort((a, b) => new Date(a.birthday) - new Date(b.birthday))
            .forEach(item => result[item.gameTeam].push(item));
        return result;
    }, [memberListInfo]);

    const currentPlayers = useMemo(() => ({
        1: teamMembers[1].filter(item => item.firstTemp === 1),
        2: teamMembers[2].filter(item => item.firstTemp === 1),
    }), [teamMembers]);

    const selectedPlayer = [...currentPlayers[1], ...currentPlayers[2]].find(item => item.id === playerId);
    const selectedGoal = GOAL_OPTIONS.find(item => item.value === goalEventId);
    const assistOptions = selectedPlayer
        ? teamMembers[selectedPlayer.gameTeam].filter(item => item.id !== selectedPlayer.id)
        : [];
    const selectedAssist = assistOptions.find(item => item.id === assistMemberId);
    const substitutionCurrent = substitutionTeam ? currentPlayers[substitutionTeam] : [];
    const substitutionBench = substitutionTeam
        ? teamMembers[substitutionTeam].filter(item => item.firstTemp === null)
        : [];

    const chooseAction = value => {
        setAction(value);
        setGoalEventId(null);
        setPlayerId(null);
        setAssistMemberId(null);
        setCardReason('');
        setCustomReason('');
        setSubstitutionTeam(null);
        setDownMemberIds([]);
        setUpMemberIds([]);
    };

    const selectPlayer = id => {
        setPlayerId(current => current === id ? null : id);
        setAssistMemberId(null);
    };

    const toggleId = (setter, id) => {
        setter(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
    };

    const submit = async () => {
        if (action === null) {
            Toast.show({ icon: 'fail', content: '请选择记录类型' });
            return;
        }

        let data;
        if (action === 3) {
            if (!goalEventId || !selectedPlayer) {
                Toast.show({ icon: 'fail', content: '请选择进球方式和进球队员' });
                return;
            }
            data = {
                gameId: gameDetail.id,
                memberId: selectedPlayer.id,
                eventId: goalEventId,
                assistMemberId: selectedGoal?.openPlay ? assistMemberId : null,
            };
        } else if ([5, 6].includes(action)) {
            const finalReason = customReason.trim() || cardReason;
            if (!finalReason || !selectedPlayer) {
                Toast.show({ icon: 'fail', content: '请选择犯规原因和球员' });
                return;
            }
            data = {
                team: action,
                gameId: gameDetail.id,
                memberId: selectedPlayer.id,
                eventId: action,
                eventInfo: finalReason,
            };
        } else if (action === 7) {
            if (!substitutionTeam) {
                Toast.show({ icon: 'fail', content: '请选择操作队伍' });
                return;
            }
            if (!downMemberIds.length || !upMemberIds.length) {
                Toast.show({ icon: 'fail', content: '请选择上下场人员' });
                return;
            }
            if (downMemberIds.length !== upMemberIds.length) {
                Toast.show({ icon: 'fail', content: '上下场人数需相等' });
                return;
            }

            const teamKey = substitutionTeam === 1 ? 'greenCurrentModel' : 'redCurrentModel';
            const backupKey = substitutionTeam === 1 ? 'greenModelConfirm' : 'redModelConfirm';
            let updatedLineup = [];
            try {
                const lineupValue = gameDetail[teamKey] || gameDetail[backupKey] || '[]';
                updatedLineup = JSON.parse(lineupValue);
                if (!Array.isArray(updatedLineup)) updatedLineup = [];
            } catch (error) {
                updatedLineup = [];
            }
            updatedLineup = JSON.parse(JSON.stringify(updatedLineup));
            downMemberIds.forEach((downId, index) => {
                const positionIndex = updatedLineup.findIndex(item => item.id === downId);
                if (positionIndex !== -1) updatedLineup[positionIndex].id = upMemberIds[index];
            });

            data = {
                gameId: gameDetail.id,
                memberId: upMemberIds.join(','),
                eventId: '17',
                upDownMemberId: downMemberIds.join(','),
                [teamKey]: JSON.stringify(updatedLineup),
            };
        } else {
            if (!selectedPlayer) {
                Toast.show({ icon: 'fail', content: '请选择球员' });
                return;
            }
            data = {
                team: action,
                gameId: gameDetail.id,
                memberId: selectedPlayer.id,
                eventId: action,
            };
        }

        setSubmitting(true);
        try {
            const response = await axios.post(API_ROUTES.game_event, data, {});
            if (response.data.code === 200) {
                Toast.show({ icon: 'success', content: action === 7 ? '轮换成功！' : '记录成功！' });
                onClose();
                onUpdated();
            } else {
                Toast.show({ icon: 'fail', content: response.data.msg });
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: '记录失败，请联系管理员！' });
        } finally {
            setSubmitting(false);
        }
    };

    const actionInfo = RECORD_ACTIONS.find(item => item.value === action);
    const summary = (() => {
        if (!actionInfo) return '请选择本次需要记录的数据';
        if (action === 3) {
            return `${selectedPlayer?.name || '待选择球员'} · ${selectedGoal?.label || '待选择进球方式'}${selectedGoal?.openPlay ? ` · 助攻：${selectedAssist?.name || '无'}` : ''}`;
        }
        if ([5, 6].includes(action)) {
            return `${selectedPlayer?.name || '待选择球员'} · ${actionInfo.label} · ${customReason.trim() || cardReason || '待选择原因'}`;
        }
        if (action === 7) {
            return substitutionTeam
                ? `已选择下场 ${downMemberIds.length} 人，上场 ${upMemberIds.length} 人`
                : '待选择操作队伍';
        }
        return `${selectedPlayer?.name || '待选择球员'} · ${actionInfo.label}`;
    })();

    return (
        <Popup
            visible={visible}
            onMaskClick={onClose}
            bodyStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '92vh', overflow: 'hidden' }}
            style={{ '--z-index': 1400 }}
        >
            <div className="referee-record-sheet">
                <div className="referee-record-header">
                    <button type="button" className="referee-record-cancel" onClick={onClose}>取消</button>
                    <div className="referee-record-heading">
                        <img src={Icon.CAIPAN_SHAOZI} alt="" />
                        <div><strong>比赛数据记录</strong><span>选择事件并确认提交</span></div>
                    </div>
                    <button type="button" className="referee-record-confirm" disabled={submitting} onClick={submit}>
                        {submitting ? '提交中' : '确认'}
                    </button>
                </div>

                <div className="referee-record-content">
                    <div className="pitch-action-section-title"><span>1</span>选择记录类型</div>
                    <div className="pitch-action-grid">
                        {RECORD_ACTIONS.map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={`${item.tone}${action === item.value ? ' active' : ''}`}
                                onClick={() => chooseAction(item.value)}
                            >
                                <img src={item.icon} alt="" />
                                <strong>{item.label}</strong>
                                {action === item.value && <em>✓</em>}
                            </button>
                        ))}
                    </div>

                    {action === 3 && (
                        <div className="referee-record-detail">
                            <div className="pitch-action-section-title"><span>2</span>选择进球方式</div>
                            <div className="pitch-goal-option-grid">
                                {GOAL_OPTIONS.map(item => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={goalEventId === item.value ? 'active' : ''}
                                        onClick={() => {
                                            setGoalEventId(item.value);
                                            setAssistMemberId(null);
                                            if ([12, 21].includes(item.value)) Toast.show({ content: '建议优先使用点球专用记录入口' });
                                        }}
                                    >
                                        <span>{item.icon}</span><strong>{item.label}</strong>
                                        {goalEventId === item.value && <em>✓</em>}
                                    </button>
                                ))}
                            </div>
                            <div className="pitch-action-section-title"><span>3</span>选择进球队员</div>
                            <TeamPlayerGroups
                                gameDetail={gameDetail}
                                teamPlayers={currentPlayers}
                                selectedId={playerId}
                                getMemberImage={getMemberImage}
                                onSelect={selectPlayer}
                            />
                            {selectedPlayer && selectedGoal?.openPlay && (
                                <div className="referee-record-assist">
                                    <div className="pitch-action-section-title"><span>4</span>选择助攻球员（可选）</div>
                                    <div className="referee-record-player-grid">
                                        <button
                                            type="button"
                                            className={`referee-record-player none${assistMemberId === null ? ' active' : ''}`}
                                            onClick={() => setAssistMemberId(null)}
                                        >
                                            <b>—</b><strong>无助攻</strong>{assistMemberId === null && <em>✓</em>}
                                        </button>
                                        {assistOptions.map(member => (
                                            <PlayerButton
                                                key={member.id}
                                                member={member}
                                                selected={assistMemberId === member.id}
                                                getMemberImage={getMemberImage}
                                                onClick={() => setAssistMemberId(member.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {[5, 6].includes(action) && (
                        <div className="referee-record-detail">
                            <CardReasonPicker
                                cardReason={cardReason}
                                customReason={customReason}
                                onReasonChange={setCardReason}
                                onCustomChange={setCustomReason}
                                danger={action === 6}
                            />
                            <div className="pitch-action-section-title"><span>3</span>选择球员</div>
                            <TeamPlayerGroups
                                gameDetail={gameDetail}
                                teamPlayers={currentPlayers}
                                selectedId={playerId}
                                getMemberImage={getMemberImage}
                                onSelect={selectPlayer}
                            />
                        </div>
                    )}

                    {action === 7 && (
                        <div className="referee-record-detail">
                            <div className="pitch-action-section-title"><span>2</span>选择操作队伍</div>
                            <TeamButtons
                                gameDetail={gameDetail}
                                selected={substitutionTeam}
                                onSelect={team => {
                                    setSubstitutionTeam(team);
                                    setDownMemberIds([]);
                                    setUpMemberIds([]);
                                }}
                            />
                            {substitutionTeam && (
                                <>
                                    <div className="pitch-action-section-title">
                                        <span>3</span>选择下场人员
                                        <b className="referee-record-count">{downMemberIds.length}人</b>
                                    </div>
                                    <MultiPlayerGrid
                                        members={substitutionCurrent}
                                        selectedIds={downMemberIds}
                                        getMemberImage={getMemberImage}
                                        onToggle={id => toggleId(setDownMemberIds, id)}
                                    />
                                    <div className="pitch-action-section-title">
                                        <span>4</span>选择上场人员
                                        <b className="referee-record-count">{upMemberIds.length}人</b>
                                    </div>
                                    <MultiPlayerGrid
                                        members={substitutionBench}
                                        selectedIds={upMemberIds}
                                        getMemberImage={getMemberImage}
                                        onToggle={id => toggleId(setUpMemberIds, id)}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {[0, 4].includes(action) && (
                        <div className="referee-record-detail">
                            <div className="pitch-action-section-title"><span>2</span>选择球员</div>
                            <TeamPlayerGroups
                                gameDetail={gameDetail}
                                teamPlayers={currentPlayers}
                                selectedId={playerId}
                                getMemberImage={getMemberImage}
                                onSelect={selectPlayer}
                            />
                        </div>
                    )}

                    <div className="pitch-action-summary">{summary}</div>
                </div>
            </div>
        </Popup>
    );
};

export default RefereeRecordPopup;
